import { useEffect, useRef, type RefObject } from "react";

// onDismiss is stored in a ref so the effect only re-runs when `isOpen`
// changes, not on every render where the caller passes a new arrow function.
const useClickOutside = <T extends HTMLElement>(
  isOpen: boolean,
  onDismiss: () => void
): RefObject<T | null> => {
  const ref = useRef<T>(null);
  const onDismissRef = useRef(onDismiss);
  useEffect(() => {
    onDismissRef.current = onDismiss;
  });

  useEffect(() => {
    if (!isOpen) return undefined;

    const onPointerDown = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) {
        onDismissRef.current();
      }
    };

    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [isOpen]);

  return ref;
};

export default useClickOutside;
